import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../lib/api.js';
import Button from './Button.jsx';
import Card from './Card.jsx';
import { PageSpinner } from './Spinner.jsx';
import PartnerApply, { PartnerStatus } from './PartnerApply.jsx';
import { isCustomerApp } from '../lib/appTarget.js';

/**
 * Gate for the driver area, for both kinds of driver.
 *
 *   approved driver (fleet or partner) → the dashboard
 *   pending / suspended partner         → their application status
 *   rejected partner                    → apply again, with the reason
 *   signed-in non-driver                → apply to become a partner
 *
 * Status comes from /api/partners/me rather than the cached user, so approval
 * shows up as soon as the app is reopened. The API enforces all of this
 * independently — this only decides which screen to show.
 */
export default function DriverRoute({ children }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'app_admin';

  const partnerQ = useQuery({
    queryKey: ['partner-me', user?.id],
    queryFn: () => api.get('/api/partners/me'),
    enabled: Boolean(user) && !isAdmin,
    // Status changes on the admin's side (approve, suspend, reinstate), so never
    // trust an old answer: re-check on every open and whenever the app returns
    // to the foreground, and keep polling until the driver is approved.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const d = q.state.data;
      const approved = d?.role === 'driver' && d?.profile?.status === 'approved';
      return approved ? false : 20_000;
    },
  });

  if (loading) return <PageSpinner label="Checking your access" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (isAdmin) return children ?? <Outlet />;

  if (partnerQ.isLoading) return <PageSpinner label="Checking your account" />;
  if (partnerQ.isError) {
    return (
      <div className="mx-auto max-w-lg px-4 pt-10">
        <Card tone="quiet">
          <p className="text-sm text-ink">We couldn’t check your account. Please try again.</p>
          <Button className="mt-4" size="sm" onClick={() => partnerQ.refetch()}>Try again</Button>
        </Card>
      </div>
    );
  }

  const { role, profile, signupEnabled } = partnerQ.data ?? {};

  if (role === 'driver') {
    if (profile?.status === 'approved') return children ?? <Outlet />;
    if (profile?.status === 'rejected') return <PartnerApply rejected={profile} />;
    return <PartnerStatus profile={profile} />;
  }

  if (role === 'customer' && signupEnabled) return <PartnerApply />;

  return (
    <div className="shell flex min-h-[60vh] flex-col items-center justify-center py-16 text-center">
      <span className="eyebrow">Drivers only</span>
      <h1 className="mt-3 font-display text-3xl text-heading">This area is for delivery staff</h1>
      <p className="mt-3 max-w-md text-sm text-ink-muted">
        {role === 'customer'
          ? 'Partner sign-up is closed right now. Please check back later.'
          : 'This account can’t be used as a driver. Sign in with your driver account instead.'}
      </p>
      {/* In the driver app "home" is this screen, so offer a way out instead. */}
      {isCustomerApp ? (
        <Button to="/" className="mt-8">Back to home</Button>
      ) : (
        <Button onClick={logout} className="mt-8">Sign out</Button>
      )}
    </div>
  );
}
